/**
 * @fileoverview エラーメッセージコンポーネント
 * @description チャットエラー時の表示
 */
import { AlertCircle, RefreshCw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * ErrorMessageのprops型
 */
interface ErrorMessageProps {
  /** エラーメッセージ */
  message?: string
  /** 再試行ボタンのコールバック */
  onRetry?: () => void
  /** 閉じるボタンのコールバック */
  onDismiss?: () => void
  /** カスタムクラス名 */
  className?: string
}

/**
 * エラーメッセージコンポーネント
 * @description チャットでエラーが発生した際に表示
 * @example
 * <ErrorMessage message="通信エラーが発生しました" onRetry={handleRetry} />
 */
export function ErrorMessage({
  message = 'エラーが発生しました。しばらくしてから再度お試しください。',
  onRetry,
  onDismiss,
  className,
}: ErrorMessageProps) {
  return (
    <div className={cn('flex flex-col items-center gap-4 rounded-xl bg-red-50 p-6', className)}>
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
        <AlertCircle className="h-6 w-6 text-red-600" />
      </div>
      <div className="text-center">
        <p className="text-sm text-red-800">{message}</p>
      </div>
      <div className="flex gap-2">
        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="gap-2 border-red-200 text-red-700 hover:bg-red-100"
          >
            <RefreshCw className="h-4 w-4" />
            再試行
          </Button>
        )}
        {onDismiss && (
          <Button
            variant="outline"
            size="sm"
            onClick={onDismiss}
            className="border-red-200 text-red-700 hover:bg-red-100"
          >
            閉じる
          </Button>
        )}
      </div>
    </div>
  )
}

/**
 * レート制限エラーコンポーネント
 * @description API利用制限に達した際に表示
 */
export function RateLimitError({
  onDismiss,
  className,
}: {
  onDismiss?: () => void
  className?: string
}) {
  return (
    <div className={cn('flex flex-col items-center gap-4 rounded-xl bg-orange-50 p-6', className)}>
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-100">
        <AlertCircle className="h-6 w-6 text-orange-600" />
      </div>
      <div className="text-center">
        <p className="font-semibold text-orange-800">本日の利用回数上限に達しました</p>
        <p className="mt-1 text-sm text-orange-700">
          ログインすると、より多くの質問ができるようになります。
        </p>
      </div>
      <div className="flex gap-2">
        <Button asChild className="gap-2 bg-orange-600 hover:bg-orange-700">
          <a href="/login">ログインして続ける</a>
        </Button>
        {onDismiss && (
          <Button
            variant="outline"
            size="sm"
            onClick={onDismiss}
            className="border-orange-200 text-orange-700 hover:bg-orange-100"
          >
            閉じる
          </Button>
        )}
      </div>
    </div>
  )
}
